import { usePreferredSideFit } from "@/components/hooks/usePreferredSideFit";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./DropdownMenu.scss";

// Keep compatibility with previous DropdownMenu users (no event param required)
type DropdownMenuProps = {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: (event?: MouseEvent | KeyboardEvent) => void;

  // New (from former Dropdown.tsx)
  withinPortal?: boolean; // default true when anchorRef exists
  portalProps?: {
    target?: HTMLElement | string; // HTMLElement or CSS selector
    id?: string; // optional, informational only
    zIndex?: number; // overrides zIndex for floating/portal path
  };

  /** Optional anchor for floating behavior. If omitted, component behaves like the old inline DropdownMenu. */
  anchorRef?: React.RefObject<HTMLElement>;

  /** Placement & offset */
  placement?: "bottom-start" | "bottom-end";
  offset?: number; // gap from anchor; default 4
} & React.HTMLAttributes<HTMLDivElement>;

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  children,
  isOpen,
  onClose,
  withinPortal = true,
  portalProps,
  anchorRef,
  placement = "bottom-start",
  offset = 4,
  ...props
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [verticalPlacement, setVerticalPlacement] = useState<"down" | "up">("down");

  // Resolve portal target
  const resolveTarget = (t?: HTMLElement | string): HTMLElement => {
    if (!t) {
      return document.body;
    }
    if (typeof t === "string") {
      const el = document.querySelector<HTMLElement>(t);
      return el ?? document.body;
    }
    return t;
  };

  // Close interactions (outside click + Escape)
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapperRef.current && !wrapperRef.current.contains(target) && !anchorRef?.current?.contains(target)) {
        onClose(event);
      }
    };

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose(event);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeydown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeydown);
    };
  }, [isOpen, onClose]);

  // Measurements for floating mode
  const { fitsDown, spaces, vNeeded } = usePreferredSideFit(anchorRef as React.RefObject<HTMLElement>, wrapperRef, {
    open: isOpen,
  });

  const vSpaces = spaces ? { up: spaces.up, down: spaces.down } : null;

  useEffect(() => {
    if (!isOpen || !vSpaces) {
      setVerticalPlacement("down");
      return;
    }

    setVerticalPlacement((current) => {
      const next =
        fitsDown && vSpaces.down >= vNeeded
          ? "down"
          : vSpaces.up >= vNeeded
            ? "up"
            : vSpaces.down >= vSpaces.up
              ? "down"
              : "up";

      const hysteresisPx = 24;
      if (current === "down" && vSpaces.down + hysteresisPx >= vSpaces.up) {
        return "down";
      }

      if (current === "up" && vSpaces.up + hysteresisPx >= vSpaces.down) {
        return "up";
      }

      return next;
    });
  }, [fitsDown, isOpen, vNeeded, vSpaces]);

  // Compute fixed positioning style for floating modes
  const style: React.CSSProperties = useMemo(() => {
    const base: React.CSSProperties = {
      position: "fixed",
      zIndex: portalProps?.zIndex ?? 1000,
    };

    const anchorEl = anchorRef?.current;
    if (!anchorEl) {
      // In theory not hit when we gate by anchorRef, but keep safe
      return { ...base, visibility: "hidden" };
    }

    const rect = anchorEl.getBoundingClientRect();
    const left = placement === "bottom-end" ? Math.round(rect.right) : Math.round(rect.left);
    const transform = placement === "bottom-end" ? "translateX(-100%)" : undefined;

    if (!vSpaces) {
      return { ...base, left, transform, visibility: "hidden" };
    }

    const gap = offset ?? 4;
    let top: number | undefined;
    let bottom: number | undefined;
    let maxHeight = 0;

    if (verticalPlacement === "down") {
      top = Math.round(rect.bottom + gap);
      maxHeight = Math.max(120, Math.floor(vSpaces.down));
    } else {
      bottom = Math.round(window.innerHeight - rect.top + gap);
      maxHeight = Math.max(120, Math.floor(vSpaces.up));
    }

    return { ...base, left, transform, top, bottom, maxHeight };
  }, [anchorRef, placement, offset, vSpaces, verticalPlacement, portalProps?.zIndex]);

  if (!isOpen) {
    return null;
  }

  // LEGACY PATH: no anchorRef -> inline flow with legacy CSS positioning
  if (!anchorRef?.current) {
    return (
      <div
        className="dropdown-menu-wrapper legacy"
        ref={wrapperRef}
        onMouseDown={(e) => e.stopPropagation()}
        role="presentation"
        style={{
          ...props.style,
        }}
      >
        <ul className="dropdown-menu" role="menu" aria-orientation="vertical">
          {children}
        </ul>
      </div>
    );
  }

  // FLOATING NODE: used for both withinPortal true/false
  const node = (
    <div
      className="dropdown-menu-wrapper"
      ref={wrapperRef}
      style={{
        ...style,
        ...props.style,
      }}
      onMouseDown={(e) => e.stopPropagation()}
      role="presentation"
    >
      <ul className="dropdown-menu" role="menu" aria-orientation="vertical">
        {children}
      </ul>
    </div>
  );

  // Inline floating (no portal)
  if (!withinPortal) {
    return node;
  }

  // Portaled floating
  const target = resolveTarget(portalProps?.target as HTMLElement | string | undefined);
  return createPortal(node, target);
};
